// Derived bitmaps the renderer reuses (architecture §9.2, art-assets §4.1, §5):
// - multiply-tinted copies of user art (pellets, ejected mass), one per (image, colour), LRU;
// - skin mip chains: each skin pre-scaled to 512, 256, 128 and 64 px and clipped to the inscribed
//   circle, so a cell draws the nearest larger level with no per-frame clip;
// - circle-clipped copies (the optional cell overlay).
//
// Everything is keyed by the source image object (WeakMap), so a reloaded image (dev hot reload)
// simply gets fresh sprites and the old ones are collected.

import type { Rgb } from '@ballio/shared';
import type { AssetImage } from '../assets/loader.js';
import { type Canvas2D, imageSize, paintCanvas } from './canvas.js';
import { css } from './theme.js';

const TAU = Math.PI * 2;

/** Skin mip sizes, largest first (art-assets §4.1). */
export const SKIN_MIP_SIZES = [512, 256, 128, 64] as const;

/** Distinct tint colours kept per source image. */
export const TINTS_PER_IMAGE = 64;

/** A small LRU map (Map insertion order = recency). */
export class LruMap<K, V> {
  readonly max: number;
  private readonly map = new Map<K, V>();

  constructor(max: number) {
    this.max = Math.max(1, Math.floor(max));
  }

  get size(): number {
    return this.map.size;
  }

  has(key: K): boolean {
    return this.map.has(key);
  }

  /** The value, marking it most recently used. */
  get(key: K): V | undefined {
    if (!this.map.has(key)) return undefined;
    const v = this.map.get(key) as V;
    this.map.delete(key);
    this.map.set(key, v);
    return v;
  }

  set(key: K, value: V): void {
    this.map.delete(key);
    this.map.set(key, value);
    while (this.map.size > this.max) {
      const oldest = this.map.keys().next();
      if (oldest.done) break;
      this.map.delete(oldest.value);
    }
  }

  delete(key: K): boolean {
    return this.map.delete(key);
  }

  clear(): void {
    this.map.clear();
  }

  keys(): IterableIterator<K> {
    return this.map.keys();
  }
}

/**
 * Multiply tint (art-assets §5): fill the image's opaque area with the tint colour, then draw the
 * image over it in `multiply` mode (white → tint, black stays black, greys become shades), and
 * finally cut back to the image's own alpha.
 */
export function tintImage(img: AssetImage, color: Rgb): Canvas2D | null {
  const { w, h } = imageSize(img);
  if (!(w > 0 && h > 0)) return null;
  return paintCanvas(w, h, (ctx) => {
    ctx.drawImage(img, 0, 0, w, h);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = css(color);
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(img, 0, 0, w, h);
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(img, 0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';
  });
}

/** A copy of `img` (centre-cropped to a square of side `size`) clipped to its inscribed circle. */
export function circleClipImage(img: CanvasImageSource, size: number): Canvas2D | null {
  const { w, h } = imageSize(img);
  const side = Math.min(w, h);
  if (!(side > 0) || !(size > 0)) return null;
  const sx = (w - side) / 2;
  const sy = (h - side) / 2;
  return paintCanvas(size, size, (ctx) => {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, TAU);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
  });
}

/** Builds the 512/256/128/64 circle-clipped mip chain of a skin (largest first). */
export function buildSkinMips(img: AssetImage): Canvas2D[] | null {
  const levels: Canvas2D[] = [];
  let src: CanvasImageSource = img;
  for (const size of SKIN_MIP_SIZES) {
    const level = circleClipImage(src, size);
    if (!level) break;
    levels.push(level);
    src = level; // halve from the previous level: better downscaling than one big jump
  }
  return levels.length > 0 ? levels : null;
}

/** The smallest level whose side is at least `diameterPx` (else the largest). */
export function pickMipLevel<T extends { readonly width: number }>(
  levels: readonly T[],
  diameterPx: number,
): T | null {
  for (let i = levels.length - 1; i >= 0; i--) {
    const level = levels[i];
    if (level && level.width >= diameterPx) return level;
  }
  return levels[0] ?? null;
}

export class SpriteCache {
  private tints = new WeakMap<object, LruMap<number, Canvas2D | null>>();
  private skins = new WeakMap<object, Canvas2D[] | null>();
  private clipped = new WeakMap<object, Canvas2D | null>();

  /** `img` multiply-tinted with `color` (null when no canvas is available). */
  tinted(img: AssetImage, color: Rgb): Canvas2D | null {
    let perImage = this.tints.get(img);
    if (!perImage) {
      perImage = new LruMap(TINTS_PER_IMAGE);
      this.tints.set(img, perImage);
    }
    if (perImage.has(color)) return perImage.get(color) ?? null;
    const sprite = tintImage(img, color);
    perImage.set(color, sprite);
    return sprite;
  }

  /** The skin's mip level for a cell `diameterPx` backing pixels across. */
  skinLevel(img: AssetImage, diameterPx: number): Canvas2D | null {
    let mips = this.skins.get(img);
    if (mips === undefined) {
      mips = buildSkinMips(img);
      this.skins.set(img, mips);
    }
    return mips ? pickMipLevel(mips, diameterPx) : null;
  }

  /** `img` clipped to its inscribed circle at its own size (cell overlay). */
  circle(img: AssetImage): Canvas2D | null {
    let c = this.clipped.get(img);
    if (c === undefined) {
      const { w, h } = imageSize(img);
      c = circleClipImage(img, Math.min(w, h));
      this.clipped.set(img, c);
    }
    return c;
  }

  clear(): void {
    this.tints = new WeakMap();
    this.skins = new WeakMap();
    this.clipped = new WeakMap();
  }
}
